/**
 * Weekend bucketing for meet discovery. Wrestling meets cluster on Fri–Sun, so
 * we group by the weekend a date belongs to. A "weekend" is anchored on its
 * Saturday; any weekday folds into the nearest upcoming weekend (Mon–Thu → that
 * same week's Sat) so nothing is dropped from the grouped view.
 */

export interface WeekendBucket {
  /** Stable key, the Saturday's YYYY-MM-DD. */
  key: string;
  /** Saturday date (local). */
  saturday: Date;
  /** Human label, e.g. "This weekend · Nov 1–2" or "Nov 8–9". */
  label: string;
}

function parseLocal(ymd: string): Date {
  // Treat as local midnight to avoid TZ drift.
  return new Date(`${ymd}T00:00:00`);
}

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

/** The Saturday of the weekend a given date belongs to. */
export function weekendSaturday(date: Date): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dow = d.getDay(); // 0 Sun ... 6 Sat
  // Sun(0) belongs to the weekend that started the day before (Sat = -1).
  // Fri(5) → +1, Sat(6) → 0, Mon–Thu(1–4) → that week's upcoming Sat.
  const delta = dow === 0 ? -1 : 6 - dow;
  d.setDate(d.getDate() + delta);
  return d;
}

/** Relative-aware label for a weekend, given "now". */
export function weekendLabel(saturday: Date, now = new Date()): string {
  const sunday = new Date(saturday);
  sunday.setDate(saturday.getDate() + 1);
  const mon = saturday.toLocaleDateString(undefined, { month: "short" });
  const monSun = sunday.toLocaleDateString(undefined, { month: "short" });
  const range =
    mon === monSun
      ? `${mon} ${saturday.getDate()}–${sunday.getDate()}`
      : `${mon} ${saturday.getDate()} – ${monSun} ${sunday.getDate()}`;

  // Relative prefix vs. the current weekend.
  const thisSat = weekendSaturday(now);
  const weeks = Math.round(
    (saturday.getTime() - thisSat.getTime()) / (7 * 86_400_000),
  );
  let prefix = "";
  if (weeks === 0) prefix = "This weekend · ";
  else if (weeks === 1) prefix = "Next weekend · ";
  else if (weeks === 2) prefix = "In 2 weeks · ";
  else if (weeks < 0) prefix = "Past · ";
  return prefix + range;
}

export function weekendBucketFor(ymdStr: string, now = new Date()): WeekendBucket {
  const sat = weekendSaturday(parseLocal(ymdStr));
  return { key: ymd(sat), saturday: sat, label: weekendLabel(sat, now) };
}

/**
 * End of the wrestling season window: the upcoming March 31. If we're already
 * past March, roll to next year's March 31 so the window always covers the
 * rest of the current/next season. Returns YYYY-MM-DD.
 */
export function seasonEndYmd(now = new Date()): string {
  const year = now.getMonth() <= 2 ? now.getFullYear() : now.getFullYear() + 1;
  return `${year}-03-31`;
}

/**
 * Group items (with a YYYY-MM-DD date accessor) into weekend buckets, ordered
 * chronologically. Returns [{bucket, items}] preserving input order within a
 * bucket.
 */
export function groupByWeekend<T>(
  items: T[],
  getDate: (t: T) => string,
  now = new Date(),
): { bucket: WeekendBucket; items: T[] }[] {
  const map = new Map<string, { bucket: WeekendBucket; items: T[] }>();
  for (const it of items) {
    const b = weekendBucketFor(getDate(it), now);
    const existing = map.get(b.key);
    if (existing) existing.items.push(it);
    else map.set(b.key, { bucket: b, items: [it] });
  }
  return [...map.values()].sort((a, b) =>
    a.bucket.key.localeCompare(b.bucket.key),
  );
}
